import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  AutocompleteValueOrFreeSoloValueMapping,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material';
import {deepEqual} from 'fast-equals';
import {nanoid} from 'nanoid/non-secure';

import type {JSX} from 'react';
import {Controller, useFormContext, useFormState} from 'react-hook-form';
import {PropsWithoutChildren} from '../react/types';

type AllowedAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'children' | 'renderInput'>;
interface AutocompleteInputFieldProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends PropsWithoutChildren<AllowedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> {
  defaultValue?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  label: string;
  minWidth?: number | string;
  name: string;
  required?: boolean;
}

export function AutocompleteInputField<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  defaultValue,
  id = nanoid(), // id required to associate label with input element
  label,
  minWidth,
  multiple,
  name,
  onBlur,
  onChange,
  required = false,
  sx,
  ...muiProps
}: AutocompleteInputFieldProps<T, Multiple, DisableClearable, FreeSolo>): JSX.Element {
  const {control} = useFormContext();
  const {errors} = useFormState();
  const error = errors[name]?.message as string | undefined;
  return (
    <Controller
      control={control}
      defaultValue={defaultValue ?? (multiple ? [] : null)}
      name={name}
      render={({field}) => {
        const helperId = nanoid();
        return (
          <FormControl fullWidth={minWidth === undefined} sx={{...sx, minWidth}}>
            <Autocomplete
              {...muiProps}
              {...field}
              aria-describedby={error && helperId}
              id={id}
              isOptionEqualToValue={(option: T, value: AutocompleteValueOrFreeSoloValueMapping<T, FreeSolo>) =>
                deepEqual(option, value)
              }
              multiple={multiple}
              onBlur={(event) => {
                field.onBlur();
                if (onBlur) {
                  onBlur(event);
                }
              }}
              onChange={(event, value, reason, details) => {
                field.onChange(value);
                if (onChange) {
                  onChange(event, value, reason, details);
                }
              }}
              renderInput={(params) => <TextField {...params} label={label} required={required} />}
            />
            {error && (
              <FormHelperText error id={helperId}>
                {error}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
